<template>
  <div>
    <navbar :links="links" />
    <v-layout class="my-4">
      <span class="grey--text text--darken-2 subheading">{{
        $t('timeframeManagementHint')
      }}</span>
    </v-layout>
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Headers -->
    <v-card
      v-if="$vuetify.breakpoint.smAndUp && !loading && orderedTimeframes.length"
      flat
    >
      <v-card-text class="py-0">
        <v-layout
          justify-center
          align-center
        >
          <span class="grow-1 body-2 pr-1">{{ $t('name') }}</span>
          <span class="grow-05 body-2 pr-1">{{ $t('weekdays') }}</span>
          <span class="grow-05 body-2 pr-1">{{ $t('timeframe') }}</span>
          <span class="grow-1 body-2 pr-1">{{ $t('group') }}</span>
          <span class="grow-1 body-2">{{ $t('contents') }}</span>
        </v-layout>
      </v-card-text>
    </v-card>
    <!-- Timeframes -->
    <div
      v-if="orderedTimeframes.length === 0 && !loading"
      class="ml-3 mt-2 mb-4 grey--text subheading"
    >
      {{ $t('noTimeFramesAvailable') }}
    </div>
    <v-card
      v-for="(timeframe, timeframeIndex) in orderedTimeframes"
      :key="timeframe.id"
      ref="timeframe-cards"
      class="my-2"
      :class="{
        'elevation-0': timeframe.id !== timeframeBeingEdited.id,
        'elevation-5': timeframe.id === timeframeBeingEdited.id,
      }"
      :style="{
        opacity:
          !!timeframeBeingEdited.id && timeframeBeingEdited.id !== timeframe.id
            ? 0.5
            : 1.0,
      }"
    >
      <v-card-text class="py-1">
        <v-layout
          :align-center="$vuetify.breakpoint.smAndUp"
          :column="$vuetify.breakpoint.xs"
        >
          <!-- Name -->
          <v-layout
            v-if="timeframe.id !== timeframeBeingEdited.id"
            wrap
            class="grow-1 pr-1"
            justify-space-between
          >
            <span
              v-if="$vuetify.breakpoint.xs"
              class="body-2 mr-1"
            >{{
              $t('name')
            }}</span>
            <span
              :title="$t('edit')"
              class="clickable"
              @click="startEditing(timeframe, timeframeIndex)"
            >{{ timeframe.name }}</span>
          </v-layout>
          <!-- Weekdays -->
          <v-layout
            v-if="timeframe.id !== timeframeBeingEdited.id"
            wrap
            class="grow-05 body-1 pr-1"
            justify-space-between
            data-cy="timeframe-weekdays-description"
          >
            <span
              v-if="$vuetify.breakpoint.xs"
              class="body-2 mr-1"
            >{{
              $t('weekdays')
            }}</span>
            <span
              :title="$t('edit')"
              class="clickable"
              @click="startEditing(timeframe, timeframeIndex)"
            >{{ describeWeekdays(timeframe.weekdays) }}</span>
          </v-layout>
          <!-- Start and end -->
          <v-layout
            v-if="timeframe.id !== timeframeBeingEdited.id"
            wrap
            class="grow-05 body-1 pr-1"
            justify-space-between
          >
            <span
              v-if="$vuetify.breakpoint.xs"
              class="body-2 mr-1"
            >{{
              $t('timeframe')
            }}</span>
            <span
              :title="$t('edit')"
              class="clickable"
              @click="startEditing(timeframe, timeframeIndex)"
            >
              {{ timeframe.start }} - {{ timeframe.end }}
              <v-icon
                v-if="timeframe.start == timeframe.end"
                color="blue"
                class="ml-1"
                small
                :title="$t('timeframeActiveOnWholeDay')"
              >
                all_inclusive
              </v-icon>
            </span>
          </v-layout>
          <!-- Group -->
          <v-layout
            v-if="timeframe.id !== timeframeBeingEdited.id"
            align-center
            justify-start
            class="grow-1 pr-1"
            justify-space-between
          >
            <span
              v-if="$vuetify.breakpoint.xs"
              class="body-2 mr-1"
            >{{
              $t('group')
            }}</span>
            <span
              :title="$t('edit')"
              class="clickable"
              @click="startEditing(timeframe, timeframeIndex)"
            >{{ timeframe.group.name }}</span>
          </v-layout>
          <!-- Buttons -->
          <v-layout
            v-if="timeframe.id !== timeframeBeingEdited.id"
            align-center
            justify-end
            class="grow-1"
            :class="{'my-2': $vuetify.breakpoint.xs}"
          >
            <!-- Number of related contents icons -->
            <v-layout
              align-baseline
              nowrap
            >
              <span
                :title="$t('timeframeRelatedContentsTooltip')"
                :class="{
                  clickable:
                    timeframe.num_radio_spots ||
                    timeframe.num_tv_spots ||
                    timeframe.num_menu_categories,
                }"
                data-cy="related-content-descriptions"
                @click="
                  timeframe.num_radio_spots ||
                    timeframe.num_tv_spots ||
                    timeframe.num_menu_categories
                    ? showRelatedContentDescriptionsDialogForTimeframe(
                      timeframe
                    )
                    : null
                "
              >
                <span
                  v-if="timeframe.num_radio_spots"
                  class="mr-3"
                  :title="$t('radioSpots')"
                >
                  <v-icon
                    small
                    color="msr"
                  >music_note</v-icon>
                  {{ timeframe.num_radio_spots }}
                </span>
                <span
                  v-if="timeframe.num_tv_spots"
                  :title="$t('tvSpots')"
                  class="mr-3"
                >
                  <v-icon
                    small
                    color="msr"
                  >tv</v-icon>
                  {{ timeframe.num_tv_spots }}
                </span>
                <span
                  v-if="timeframe.num_menu_categories"
                  :title="$t('menuCategories')"
                >
                  <v-icon
                    small
                    color="msr"
                  >menu_book</v-icon>
                  {{ timeframe.num_menu_categories }}
                </span>
                <span
                  v-if="
                    !timeframe.num_radio_spots &&
                      !timeframe.num_tv_spots &&
                      !timeframe.num_menu_categories
                  "
                  class="grey--text"
                >
                  {{ $t('none') }}
                </span>
              </span>
            </v-layout>
            <!-- Edit Button -->
            <v-btn
              round
              :class="{'mr-0': $vuetify.breakpoint.xs}"
              icon
              flat
              color="msr"
              :title="$t('edit')"
              :disabled="!timeframe.can_be_modified_by_user || currentlyEditing"
              data-cy="timeframe-edit-btn"
              @click="startEditing(timeframe, timeframeIndex)"
            >
              <v-icon> edit </v-icon>
            </v-btn>
            <!-- Copy/Duplicate Button -->
            <v-btn
              round
              :class="{'mr-0': $vuetify.breakpoint.xs}"
              icon
              flat
              color="msr"
              :title="$t('duplicate')"
              :disabled="!timeframe.can_be_modified_by_user || currentlyEditing"
              @click="duplicateTimeframe(timeframe)"
            >
              <v-icon>content_copy</v-icon>
            </v-btn>
            <!-- Remove Button -->
            <v-btn
              round
              :class="{'mr-0': $vuetify.breakpoint.xs}"
              icon
              flat
              color="msr"
              :title="$t('remove')"
              :disabled="!timeframe.can_be_modified_by_user || currentlyEditing"
              data-cy="timeframe-remove-btn"
              @click="removeTimeframe(timeframe)"
            >
              <v-icon> delete </v-icon>
            </v-btn>
          </v-layout>
          <!-- Editing controls for currently edited TimeFrame -->
          <v-layout
            v-if="timeframe.id === timeframeBeingEdited.id"
            :column="$vuetify.breakpoint.xs"
            align-center
            class="mt-3 mb-4"
          >
            <timeframe-widget
              ref="timeframe-widget"
              class="grow-2"
              :timeframe-start.sync="timeframeBeingEdited.start"
              :timeframe-end.sync="timeframeBeingEdited.end"
              :timeframe-weekdays.sync="timeframeBeingEdited.weekdays"
              :timeframe-name.sync="timeframeBeingEdited.name"
              :timeframe-group-id.sync="timeframeBeingEdited.group.id"
              @submit="saveEditing()"
            />
            <v-layout
              class="grow-1"
              column
              :class="{'full-width': $vuetify.breakpoint.xs}"
            >
              <div
                v-if="timeframe.num_tv_spots || timeframe.num_radio_spots"
                class="warning--text mb-2 ml-3"
              >
                <strong>{{ getUpdateHintText(timeframe) }}</strong>
              </div>
              <v-layout
                justify-center
                wrap
              >
                <btn-abort
                  round
                  :disabled="busy"
                  @click="cancelEditing()"
                />
                <btn-ok
                  round
                  data-cy="timeframe-confirm-edit-btn"
                  :disabled="!timeframeBeingEditedIsValid || busy"
                  @click="saveEditing()"
                />
              </v-layout>
            </v-layout>
          </v-layout>
        </v-layout>
        <v-divider
          v-if="
            timeframe.id !== timeframeBeingEdited.id &&
              timeframeIndex < orderedTimeframes.length - 1
          "
        />
      </v-card-text>
    </v-card>
    <!-- Create new timeframe button -->
    <v-fade-transition>
      <v-layout
        v-if="!loading"
        justify-end
      >
        <v-btn
          round
          color="msr"
          dark
          :fixed="useFixedPositionForCreationButton"
          bottom
          right
          :disabled="showCreateTimeframeDialog"
          :class="$desktopModeStyles"
          class="z-index-3"
          data-cy="create-new-timeframe-btn"
          :loading="busy"
          @click="showCreateTimeframeDialog = true"
        >
          <v-icon left>
            add
          </v-icon>
          {{ $t('createNewTimeframe') }}
        </v-btn>
      </v-layout>
    </v-fade-transition>
    <!-- Scroll space -->
    <div style="min-height: 100px" />
    <!-- Create Timeframe Dialog -->
    <create-timeframe-dialog
      :show.sync="showCreateTimeframeDialog"
      @created="onCreateTimeframeConfirmed"
    />
    <!-- Timeframe related Contents Dialog -->
    <timeframe-related-contents-dialog
      :show.sync="showTimeframeRelatedContentDescriptionsDialog"
      :timeframe="timeframeBeingLookedAtForRelatedContents"
    />
  </div>
</template>

<script>
import {deepCopyObject} from '@/utils';
import orderBy from 'lodash.orderby';

import {describeWeekdays} from '@/mixins/weekdays';

import BtnAbort from '@/components/ButtonAbort';
import BtnOk from '@/components/ButtonOk';
import CreateTimeframeDialog from '@/components/CreateTimeframeDialog';
import Navbar from '@/components/Navbar';
import TimeframeRelatedContentsDialog from '@/components/TimeframeRelatedContentsDialog';
import TimeframeWidget from '@/components/TimeframeWidget';

const defaults = {
  timeframeBeingEdited: {
    id: null,
    start: '',
    end: '',
    name: '',
    weekdays: [],
    group: {
      id: null,
    },
  },
};

export default {
  components: {
    BtnAbort,
    BtnOk,
    CreateTimeframeDialog,
    Navbar,
    TimeframeRelatedContentsDialog,
    TimeframeWidget,
  },
  data() {
    return {
      loading: false,
      busy: false,
      links: ['dashboard', 'timeframeManagement'],
      showCreateTimeframeDialog: false,
      selectedUploadGroupId: null,
      timeframeBeingEdited: deepCopyObject(defaults.timeframeBeingEdited),
      timeframeBeingLookedAtForRelatedContents: null,
      showTimeframeRelatedContentDescriptionsDialog: false,
    };
  },
  computed: {
    useFixedPositionForCreationButton() {
      return this.orderedTimeframes.length > 0;
    },
    timeframes() {
      return this.$store.state.common.timeframes;
    },
    orderedTimeframes() {
      return orderBy(this.timeframes, ['name', 'weekdays', 'start', 'end']);
    },
    currentlyEditing() {
      return this.timeframeBeingEdited && this.timeframeBeingEdited.id !== null;
    },
    timeframeBeingEditedIsValid() {
      return (
        !!this.timeframeBeingEdited.name &&
        !!this.timeframeBeingEdited.name.trim() &&
        this.timeframeBeingEdited.group.id &&
        this.timeframeBeingEdited.start &&
        this.timeframeBeingEdited.end &&
        this.timeframeBeingEdited.weekdays.length > 0
      );
    },
  },
  mounted() {
    this.fetchTimeframes();
  },
  methods: {
    describeWeekdays: describeWeekdays,
    showRelatedContentDescriptionsDialogForTimeframe(timeframe) {
      this.timeframeBeingLookedAtForRelatedContents = timeframe;
      this.showTimeframeRelatedContentDescriptionsDialog = true;
    },
    getDeletionHintText(timeframe) {
      const numActiveEntries =
        timeframe.num_radio_spots +
        timeframe.num_tv_spots +
        timeframe.num_menu_categories;
      const deletionHintText = numActiveEntries
        ? this.$t('timeframeDeletionHint').replace(
            '{{ num }}',
            numActiveEntries
          )
        : '';
      return deletionHintText;
    },
    getUpdateHintText(timeframe) {
      const numActiveEntries =
        timeframe.num_radio_spots +
        timeframe.num_tv_spots +
        timeframe.num_menu_categories;
      const updateHintText = numActiveEntries
        ? this.$t('timeframeUpdateHint').replace('{{ num }}', numActiveEntries)
        : '';
      return updateHintText;
    },
    resetTimeframeBeingEdited() {
      this.timeframeBeingEdited = deepCopyObject(defaults.timeframeBeingEdited);
    },
    startEditing(timeframe, timeframeIndex) {
      this.timeframeBeingEdited = deepCopyObject(timeframe);

      const timeframeCard = this.$refs['timeframe-cards'][timeframeIndex];
      if (timeframeCard) {
        this.$nextTick(() => {
          timeframeCard.$el.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
          });
        });
      }

      this.$nextTick(() => {
        const timeframeWidget = this.$refs['timeframe-widget'][0];
        if (timeframeWidget) {
          this.$nextTick(() => {
            timeframeWidget.$refs['timeframe-name-input'].focus();
          });
        }
      });
    },
    cancelEditing() {
      this.resetTimeframeBeingEdited();
    },
    saveEditing() {
      this.busy = true;
      this.$api
        .updateTimeframe(this.timeframeBeingEdited)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'genericEntryUpdateSuccess',
            translate: true,
          });

          this.fetchTimeframes(false);
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.resetTimeframeBeingEdited();
          this.busy = false;
        });
    },
    duplicateTimeframe(timeframe) {
      const copiedTimeframe = Object.assign({}, timeframe);
      delete copiedTimeframe.id;

      const originalName = copiedTimeframe.name;
      let newName = originalName;
      do {
        newName = newName + ' (' + this.$t('aCopy') + ')';
      } while (this.timeframes.map((x) => x.name).includes(newName));
      copiedTimeframe.name = newName;

      this.$api.createTimeframe(copiedTimeframe).then(() => {
        this.fetchTimeframes(false);
      });
    },
    onCreateTimeframeConfirmed(newTimeframe) {
      this.busy = true;
      this.$api
        .createTimeframe(newTimeframe)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'timeframeCreatedSuccess',
            translate: true,
          });

          this.fetchTimeframes(false);
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    removeTimeframe(timeframe) {
      const vm = this;

      function submitRequestToRemoveTimeframeAndCloseDialog() {
        vm.busy = true;
        vm.$api
          .deleteTimeframe(timeframe.id)
          .then(() => {
            vm.fetchTimeframes(false);
            this.hideDialog_();
          })
          .catch((error) => {
            console.error(error);

            vm.$bus.$emit('alert-error', {
              message: 'genericFailFeedback',
              translate: true,
            });
          })
          .finally(() => {
            vm.busy = false;
          });
      }

      const deletionHintText = this.getDeletionHintText(timeframe);
      const weekdaysDescription = describeWeekdays(timeframe.weekdays);

      this.$bus.$emit('show-dynamic-dialog', {
        title: this.$t('timeframeDeletionTitle'),
        titleIcon: 'delete',
        titleIconClass: 'error--text',
        content: `
          <div class="subheading">
            <p>
              <b>${timeframe.name}, ${weekdaysDescription},
                ${timeframe.start} - ${timeframe.end}</b>
            </p>
            <p class="warning--text">
              <b>${deletionHintText}</b>
            </p>
            <p>
              ${this.$t('timeframeDeletionConfirm')}
            </p>
          </div>
        `,
        onConfirm: submitRequestToRemoveTimeframeAndCloseDialog,
      });

      // Focus confirmation button so pressing Enter submits it.
      this.$nextTick(() => {
        const el = document.querySelector(
          '[data-cy=dynamic-dialog-btn-confirm]'
        );
        if (el) {
          el.focus();
        }
      });
    },
    fetchTimeframes(spinner = true) {
      if (spinner) {
        this.loading = true;
      }
      return this.$store.dispatch('LIST_TIMEFRAMES').finally(() => {
        this.loading = false;
      });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Timeframes.vue